import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6ovdumancq2eie825r0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 md:py-40 px-4 sm:px-6 bg-gradient-to-b from-[#1e40af] to-[#1e3a8a]">
      <div className="max-w-3xl mx-auto">
        {/* Form Container */}
        <div className="bg-white rounded-2xl p-6 sm:p-10 md:p-16 shadow-2xl">
          {/* Form Header */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-3">Get Your Free Quote</h2>
            <p className="text-[#6B6B6B] text-sm sm:text-base">We'll respond within 24 hours</p>
          </div>

          {/* Form */}
          <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full h-12 sm:h-14 px-4 sm:px-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-transparent text-sm sm:text-base"
              />
            </div>

            <div>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full h-12 sm:h-14 px-4 sm:px-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-transparent text-sm sm:text-base"
              />
            </div>

            <div>
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full h-12 sm:h-14 px-4 sm:px-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-transparent text-sm sm:text-base"
              />
            </div>

            <div>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full h-12 sm:h-14 px-4 sm:px-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-transparent text-gray-700 text-sm sm:text-base"
              >
                <option value="">Select Service Type</option>
                <option value="plumbing">Plumbing Services</option>
                <option value="hvac">HVAC Installation & Repair</option>
                <option value="electrical">Electrical Work</option>
                <option value="landscaping">Landscaping & Lawn Care</option>
                <option value="roofing">Roof Repair</option>
                <option value="remodeling">Home Remodeling</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <textarea
                name="message"
                placeholder="Tell us about your project..."
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-transparent resize-none text-sm sm:text-base"
              ></textarea>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 sm:h-14 bg-[#1e40af] text-white font-bold rounded-lg hover:bg-[#1e3a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              {isSubmitting ? 'Sending...' : 'Request Free Quote'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-center text-sm">
                Thank you! We'll contact you within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-center text-sm">
                Something went wrong. Please try again or call us directly.
              </div>
            )}
          </form>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 md:mt-12 pt-8 md:pt-12 border-t border-gray-200">
            <div className="text-center">
              <div className="w-11 h-11 bg-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-phone-line text-xl text-white"></i>
              </div>
              <p className="text-[#2C2C2C] font-semibold text-sm sm:text-base">(555) 123-4567</p>
            </div>
            <div className="text-center">
              <div className="w-11 h-11 bg-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-mail-line text-xl text-white"></i>
              </div>
              <p className="text-[#2C2C2C] font-semibold text-sm sm:text-base break-all">info@homeservices.com</p>
            </div>
            <div className="text-center">
              <div className="w-11 h-11 bg-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-xl text-white"></i>
              </div>
              <p className="text-[#2C2C2C] font-semibold text-sm sm:text-base">Mon-Fri: 7AM-7PM</p>
            </div>
          </div>
        </div>

        {/* Service Area */}
        <div className="mt-16 md:mt-24 text-center text-white">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">Proudly Serving Your Community</h3>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto">
            We provide professional home services to homeowners throughout the greater metropolitan area and surrounding communities. Our local team is ready to help with all your home improvement needs.
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {['Springfield','Riverside','Lakewood','Greenville','Fairview','Oakdale','Hillside','Meadowbrook'].map(city => (
              <span key={city} className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">{city}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}